import { ReactNode, RefObject, useEffect, useMemo, useRef } from 'react';

import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import styled from 'styled-components';

import {
  Email as EmailExtension,
  getPlaceholderHtml as getEmailPlaceholderHtml,
  transformTextToEmailNode,
} from '@components/email-field/extensions';
import {
  DatePlaceholder,
  getPlaceholderHtml as getDatePlaceholderHtml,
} from '@components/field-mapping-date-picker/extensions';
import {
  NumberPlaceholder,
  getPlaceholderHtml as getNumberPlaceholderHtml,
} from '@components/field-mapping-number-field/extensions';
import {
  Phone as PhonePlaceholder,
  getPlaceholderHtml as getPhonePlaceholderHtml,
} from '@components/field-mapping-phone-field/extensions';
import { tag as SELECT_PLACEHOLDER_TAG } from '@components/field-mapping-select-field/extensions';
import { FieldType } from '@components/form/v2';
import { IconButton } from '@components/icon-button';
import { IconAddSmall } from '@components/icons/add-small';
import { Select } from '@components/select';
import { SelectMulti } from '@components/selectmulti';
import { hideSidebar, useIsSidebarActive } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingTextEditor } from '@components/text-editors';
import {
  FieldMappingEditorValue,
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType as MappingFieldType,
  getManualDefaultValue,
  mapVariableGroups,
  stripPlaceholdersToValue,
  transformOutput,
} from '@components/text-editors/addons/field-mapping';
import { MaxLength } from '@components/text-editors/addons/max-length';
import { SingleField } from '@components/text-editors/addons/single-field';
import {
  DateManualRestrictions,
  FIELD_MAPPING_SIDEBAR_ID,
  NumberManualRestrictions,
  useStandardFieldMappingSidebar,
} from '@components/text-editors/field-mapping-sidebar';
import { EditorMethods } from '@components/text-editors/text-editor';
import { useTimeoutState } from '@hooks/use-timeout-state';
import { getScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { formatNumber } from '@utils/numbers';
import { formatPhoneNumber } from '@utils/strings';

import { useFormBuilderContext } from './context';
import { FieldMappingDisplay } from './fields/use-field-mapping';
import { useFieldOptions } from './fields/use-field-options';
import { FieldContainingOptions, StateField } from './state';

const SHORT_TEXT_MAX_LENGTH = 255;

export const capShortTextValue = (value: FieldMappingEditorValue): FieldMappingEditorValue => {
  if (!value.value || value.value.length <= SHORT_TEXT_MAX_LENGTH) return value;
  const truncated = value.value.slice(0, SHORT_TEXT_MAX_LENGTH);
  const lastOpen = truncated.lastIndexOf('{{');
  const lastClose = truncated.lastIndexOf('}}');
  const safe = lastOpen > lastClose ? truncated.slice(0, lastOpen).trimEnd() : truncated;
  return { ...value, value: safe };
};

/**
 * Wraps a raw stored value (e.g. "10000", "2026-01-15T..." or "foo@bar.com")
 * back into the placeholder HTML the editor expects, so the green pill
 * round-trips on remount. No-op when the value already contains HTML or when
 * the field type isn't a single-item placeholder type.
 */
export const reconstructManualPlaceholder = (
  value: string,
  fieldType: MappingFieldType
): string => {
  if (!value || value.includes('<') || value.includes('{{')) return value;
  switch (fieldType) {
    case 'number':
    case 'decimal': {
      const num = Number(value);
      if (!Number.isFinite(num)) return value;
      return getNumberPlaceholderHtml({ value, label: formatNumber(num) });
    }
    case 'date': {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return getDatePlaceholderHtml({ value, label: abbreviateDate(date, 'en') });
    }
    case 'email':
      return getEmailPlaceholderHtml({ value });
    case 'phone':
      return getPhonePlaceholderHtml({ value, label: formatPhoneNumber(value) });
    default:
      return value;
  }
};

/**
 * Tiptap extensions a `FieldMappingTextEditor` needs so that manual-value
 * pills parse and render correctly. Pair with `stripPlaceholdersToValue` on
 * save and `reconstructManualPlaceholder` on read so storage stays as the
 * raw simple value.
 */
export const getManualPlaceholderExtensions = (fieldType: MappingFieldType) => {
  switch (fieldType) {
    case 'number':
    case 'decimal':
      return [NumberPlaceholder, SingleField];
    case 'date':
      return [DatePlaceholder, SingleField];
    case 'phone':
      return [PhonePlaceholder, SingleField];
    case 'email':
      return [EmailExtension.configure({ single: true })];
    default:
      return undefined;
  }
};

export const clearMappedNumberDefaultIfInvalid = (
  mappedDefault: FieldMappingEditorValue | undefined,
  restrictions: {
    min?: number;
    max?: number;
    disableNegative?: boolean;
    disableDecimal?: boolean;
  },
  setMappedDefault: (value: FieldMappingEditorValue) => void
): void => {
  if (!mappedDefault || typeof mappedDefault === 'string' || !mappedDefault.value) return;
  if (restrictions.disableDecimal) {
    const variables = mappedDefault.variables ?? {};
    const hasDecimalVariable = Object.values(variables).some(
      (variable) => variable?.type === 'decimal'
    );
    if (hasDecimalVariable) {
      setMappedDefault({ value: '', variables: {} });
      return;
    }
  }
  const manualValue = getManualDefaultValue(mappedDefault);
  if (manualValue == null) return;
  const num = Number(manualValue);
  if (!Number.isFinite(num)) return;
  const violates =
    (restrictions.disableDecimal && !Number.isInteger(num)) ||
    (restrictions.disableNegative && num < 0) ||
    (restrictions.min != null && num < restrictions.min) ||
    (restrictions.max != null && num > restrictions.max);
  if (violates) {
    setMappedDefault({ value: '', variables: {} });
  }
};

/**
 * Clears `mappedDefaultValue` on a date field when its current manual value
 * violates the supplied restrictions.
 */
export const clearManualDateDefaultIfInvalid = (
  mappedDefault: FieldMappingEditorValue | undefined,
  restrictions: DateManualRestrictions | undefined,
  setMappedDefault: (value: FieldMappingEditorValue) => void
): void => {
  const manualValue = getManualDefaultValue(mappedDefault);
  if (!restrictions || manualValue == null) return;
  const date = new Date(manualValue);
  if (Number.isNaN(date.getTime())) return;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const violates =
    (restrictions.disablePastDates && date < startOfToday) ||
    (restrictions.disableFutureDates && date > startOfToday) ||
    (restrictions.minDate && date < restrictions.minDate) ||
    (restrictions.maxDate && date > restrictions.maxDate);
  if (violates) {
    setMappedDefault({ value: '', variables: {} });
  }
};

type InputStateField = Extract<StateField, { kind: `input-${string}` }>;

export const extractManualSelectValue = (content: string): string | null => {
  const html = new DOMParser().parseFromString(content, 'text/html');
  const placeholder = html.getElementsByTagName(SELECT_PLACEHOLDER_TAG)[0];
  return placeholder ? placeholder.getAttribute('value') ?? '' : null;
};

export const hasMappedDefault = (
  mappedDefault: FieldMappingEditorValue | undefined
): mappedDefault is FieldMappingEditorValue =>
  mappedDefault != null && typeof mappedDefault !== 'string' && Boolean(mappedDefault.value);

type SelectionStateField = Extract<
  StateField,
  { kind: 'input-single-choice' | 'input-multiple-choice' | 'input-dropdown' }
>;

const DigitsOnly = Extension.create({
  name: 'digitsOnly',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('digitsOnly'),
        props: {
          handleTextInput: (_view, _from, _to, text) => !/^\d+$/.test(text),
          transformPastedText: (text) => text.replace(/\D/g, ''),
        },
      }),
    ];
  },
});

export const fieldKindToMappingType: Record<string, MappingFieldType> = {
  'input-text-small': 'text',
  'input-text-large': 'paragraph',
  'input-number': 'number',
  'input-date': 'date',
  'input-email': 'email',
  'input-phone': 'phone',
  'input-kennitala': 'kennitala',
  'input-file': 'file',
  'input-single-choice': 'select',
  'input-dropdown': 'select',
  'input-multiple-choice': 'multi_select',
};

const compatibleTypes: Record<string, Set<string>> = {
  text: new Set(['text', 'email', 'phone', 'number', 'decimal', 'date', 'select']),
  paragraph: new Set([
    'text',
    'email',
    'phone',
    'paragraph',
    'number',
    'decimal',
    'date',
    'select',
  ]),
  email: new Set(['email']),
  phone: new Set(['phone']),
  number: new Set(['number']),
  decimal: new Set(['number', 'decimal']),
  date: new Set(['date']),
  file: new Set(['file', 'file_multi']),
  kennitala: new Set(['text']),
  select: new Set(['select']),
  multi_select: new Set(['multi_select']),
  boolean: new Set(['boolean']),
};

export const filterVariableGroupsByType = (
  groups: FieldMappingGroup[],
  fieldType: string
): FieldMappingGroup[] => {
  const allowed = compatibleTypes[fieldType];
  if (!allowed) {
    return groups;
  }

  return groups
    .map((group) => {
      const filteredFields = group.fields.filter((field) => {
        if (field.type === 'separator') {
          return true;
        }
        return field.type != null && allowed.has(field.type);
      });

      if (filteredFields.length === 0) {
        return null;
      }

      return { ...group, fields: filteredFields };
    })
    .filter(Boolean) as FieldMappingGroup[];
};

const SINGLE_ITEM_FIELD_TYPES = new Set<string>(['number', 'decimal', 'date', 'email', 'phone']);

const SIDEBAR_ON_CLICK_FIELD_TYPES = new Set<string>([
  'number',
  'decimal',
  'date',
  'email',
  'phone',
  'file',
]);

const useDefaultValueMappingBase = (field: StateField, defaultFieldType?: MappingFieldType) => {
  const { store, variableGroups } = useFormBuilderContext();
  const formType = store.useFormType();
  const formVariant = store.useFormVariant();
  const isInputForm = (formType === 'form' || formType === 'submission') && formVariant === 'input';
  const actions = store.actions;
  const isFieldSelected = store.useIsFieldSelected(field.id);
  const isFieldMappingSidebarActive = useIsSidebarActive(FIELD_MAPPING_SIDEBAR_ID);
  const showStandardFieldMappingSidebar = useStandardFieldMappingSidebar();
  const prevIsFieldSelectedRef = useRef(isFieldSelected);

  useEffect(() => {
    const wasSelected = prevIsFieldSelectedRef.current;
    if (wasSelected && !isFieldSelected && isFieldMappingSidebarActive) {
      hideSidebar();
    }
    prevIsFieldSelectedRef.current = isFieldSelected;
  }, [isFieldSelected, isFieldMappingSidebarActive]);

  const baseFieldType = fieldKindToMappingType[field.kind] ?? defaultFieldType ?? 'text';
  const fieldType: MappingFieldType =
    baseFieldType === 'number' && 'isDecimal' in field && field.isDecimal
      ? 'decimal'
      : baseFieldType;
  const inputField = field as InputStateField;
  const mappedDefault = inputField.mappedDefaultValue;

  return {
    store,
    actions,
    inputField,
    variableGroups,
    isInputForm,
    fieldType,
    mappedDefault,
    showStandardFieldMappingSidebar,
  };
};

type DefaultValueMappingProps = {
  field: StateField;
};

export const DefaultValueMapping = ({ field }: DefaultValueMappingProps) => {
  const {
    store,
    actions,
    inputField,
    variableGroups,
    isInputForm,
    fieldType,
    mappedDefault,
    showStandardFieldMappingSidebar,
  } = useDefaultValueMappingBase(field);
  const isEditMode = store.useIsEditMode();
  const [maxLengthError, setMaxLengthError] = useTimeoutState<string | undefined>({
    defaultValue: undefined,
    timeout: 5000,
  });
  const editorRef = useRef<EditorMethods>(null);
  const singleItemEditorRef = useRef<EditorMethods>({
    insertContent: (content: string) => {
      editorRef.current?.replaceContent(content);
    },
    replaceContent: (content: string) => {
      editorRef.current?.replaceContent(content);
    },
  }) as RefObject<EditorMethods>;

  const allowMultipleFiles =
    fieldType === 'file' && 'allowMultiple' in field && field.allowMultiple;

  const filteredGroups = useMemo(() => {
    if (!variableGroups?.length) {
      return [];
    }
    const groups = filterVariableGroupsByType(variableGroups, fieldType);
    if (fieldType === 'file' && !allowMultipleFiles) {
      return groups
        .map((group) => ({
          ...group,
          fields: group.fields.filter((f) => f.type !== 'file_multi'),
        }))
        .filter((group) => group.fields.length > 0);
    }
    return groups;
  }, [variableGroups, fieldType, allowMultipleFiles]);

  const dateRestrictions: DateManualRestrictions | undefined =
    field.kind === 'input-date'
      ? {
          disablePastDates: field.dateRestrictionType === 'disablePast',
          disableFutureDates: field.dateRestrictionType === 'disableFuture',
          minDate:
            field.dateRestrictionType === 'range' && field.dateRangeStart
              ? new Date(field.dateRangeStart)
              : undefined,
          maxDate:
            field.dateRestrictionType === 'range' && field.dateRangeEnd
              ? new Date(field.dateRangeEnd)
              : undefined,
        }
      : undefined;

  const numberRestrictions: NumberManualRestrictions | undefined =
    field.kind === 'input-number'
      ? {
          min: field.restrictMin && field.min != null ? field.min : undefined,
          max: field.restrictMax && field.max != null ? field.max : undefined,
          disableNegative: !field.allowNegative,
        }
      : undefined;

  if (!isInputForm) {
    return null;
  }

  const getFieldValue = (): FieldMappingEditorValue => {
    if (!mappedDefault || typeof mappedDefault === 'string') {
      return { value: '', variables: mapVariableGroups(filteredGroups) };
    }
    return {
      ...mappedDefault,
      value: reconstructManualPlaceholder(mappedDefault.value, fieldType),
      variables: { ...mapVariableGroups(filteredGroups), ...mappedDefault.variables },
    };
  };

  const handleChange = (value: FieldMappingEditorValue) => {
    const stored = stripPlaceholdersToValue(value);
    const capped = fieldType === 'text' ? capShortTextValue(stored) : stored;
    actions.createChangeHandler(inputField, 'mappedDefaultValue')(capped);
  };

  const initialValue = getFieldValue();

  const isSingleFile = fieldType === 'file' && !('allowMultiple' in field && field.allowMultiple);
  const isSingleItem = SINGLE_ITEM_FIELD_TYPES.has(fieldType) || isSingleFile;

  const isSidebarOnClick = SIDEBAR_ON_CLICK_FIELD_TYPES.has(fieldType);

  const customShowFieldMappingSidebar: (props?: { variable?: FieldMappingFieldItem }) => void = (
    props
  ) => {
    showStandardFieldMappingSidebar({
      editorRef: isSingleItem ? singleItemEditorRef : editorRef,
      variableGroups: filteredGroups,
      fieldMappingId: `${field.id}-default`,
      fieldType: fieldType as MappingFieldType,
      label: t('defaultValue'),
      isStandardSidebar: true,
      dateRestrictions,
      numberRestrictions,
      ...props,
    });
  };

  return (
    <Stack space={2}>
      <Text font="semibold14" color="textSecondary" text={t('defaultValue')} />
      <div onClick={isSidebarOnClick ? () => customShowFieldMappingSidebar() : undefined}>
        <FieldMappingTextEditor
          ref={editorRef}
          defaultValue={initialValue}
          onChange={handleChange}
          hideLabel
          error={maxLengthError}
          variableGroups={filteredGroups}
          fieldMappingId={`${field.id}-default`}
          fieldType={fieldType as MappingFieldType}
          plaintext
          singleLine={fieldType !== 'paragraph'}
          key={`default-mapping-${field.kind}-${field.id}-${
            isSidebarOnClick ? mappedDefault?.value ?? '' : ''
          }-${Object.keys(mappedDefault?.variables ?? {}).join(',')}`}
          customShowFieldMappingSidebar={customShowFieldMappingSidebar}
          isEditMode={isEditMode}
          disabled={isSidebarOnClick}
          onEditorBlur={fieldType === 'email' ? transformTextToEmailNode : undefined}
          extensions={
            fieldType === 'number' || fieldType === 'decimal'
              ? [NumberPlaceholder, SingleField]
              : fieldType === 'date'
              ? [DatePlaceholder, SingleField]
              : fieldType === 'phone'
              ? [PhonePlaceholder, SingleField]
              : fieldType === 'email'
              ? [EmailExtension.configure({ single: true })]
              : fieldType === 'kennitala'
              ? [DigitsOnly]
              : fieldType === 'text'
              ? [
                  MaxLength.configure({
                    maxLength: SHORT_TEXT_MAX_LENGTH,
                    onReject: () =>
                      setMaxLengthError(t('maxLengthError', { limit: SHORT_TEXT_MAX_LENGTH })),
                  }),
                ]
              : undefined
          }
          actions={
            <Stack orientation="horizontal" space={2}>
              <IconButton
                icon={<IconAddSmall />}
                variant="blue-white"
                size="sm"
                onClick={() => customShowFieldMappingSidebar()}
                data-cy={`${field.kind}-default-mapping-open`}
              />
            </Stack>
          }
        />
      </div>
    </Stack>
  );
};

type DefaultValueMappingDisplayProps = {
  field: StateField | FieldType;
  children: ReactNode;
  overlayLeft?: string;
  overlayRight?: string;
  overlayHeight?: string;
  multiLine?: boolean;
  onClear?: () => void;
  isHidden?: boolean;
};

const FIELDS_WITH_RIGHT_ICON: ReadonlySet<string> = new Set(['input-date', 'input-dropdown']);

export const DefaultValueMappingDisplay = ({
  field,
  children,
  overlayLeft,
  overlayRight,
  overlayHeight,
  multiLine,
  onClear,
  isHidden = false,
}: DefaultValueMappingDisplayProps) => {
  const mappedDefault =
    'mappedDefaultValue' in field
      ? (field.mappedDefaultValue as FieldMappingEditorValue)
      : undefined;

  const placeholderFieldKinds = new Set([
    'input-number',
    'input-date',
    'input-email',
    'input-phone',
  ]);
  const isManualForPlaceholderField =
    placeholderFieldKinds.has(field.kind) && getManualDefaultValue(mappedDefault) != null;

  const showPill =
    !isHidden &&
    !isManualForPlaceholderField &&
    mappedDefault !== undefined &&
    typeof mappedDefault !== 'string' &&
    Boolean(mappedDefault?.value);

  if (!showPill) {
    return <>{children}</>;
  }

  const overlayStyle: React.CSSProperties = {
    ...(overlayLeft ? { left: overlayLeft } : {}),
    ...(overlayHeight
      ? { height: overlayHeight, alignItems: 'flex-start', paddingTop: pxToRem(8) }
      : {}),
  };

  const Content = multiLine ? PillContentMultiLine : PillContent;

  const resolvedRightPadding =
    overlayRight ?? (FIELDS_WITH_RIGHT_ICON.has(field.kind) ? theme.base.height : undefined);

  return (
    <MappedFieldWrapper data-show-pill={showPill}>
      {children}
      {showPill && mappedDefault && (
        <PillOverlay
          $rightPadding={resolvedRightPadding}
          style={Object.keys(overlayStyle).length > 0 ? overlayStyle : undefined}
        >
          <Content>
            <FieldMappingDisplay
              content={mappedDefault}
              textProps={{ font: 'regular16' }}
              onRemove={onClear}
            />
          </Content>
        </PillOverlay>
      )}
    </MappedFieldWrapper>
  );
};

const MappedFieldWrapper = styled.div({
  position: 'relative',
  // Hide placeholders/icons only while the pill is rendered — otherwise the
  // bare field is fully visible (placeholders, calendar icons, etc.).
  '&[data-show-pill="true"] input::placeholder, &[data-show-pill="true"] textarea::placeholder': {
    color: 'transparent !important',
  },
  '&[data-show-pill="true"] [data-placeholder="true"]': {
    color: 'transparent !important',
  },
  '&[data-show-pill="true"] button[type="button"] > svg': {
    color: 'transparent !important',
  },
  '&[data-show-pill="true"] [class*="-placeholder"]': {
    color: 'transparent !important',
  },
});

/**
 * Returns the mapped default pill for use as a label in selection field options, or null.
 * Renders as an extra (selected) option at the top of the radio/checkbox list.
 */
export const useMappedDefaultPill = (field: FieldType): JSX.Element | null => {
  const mappedDefault =
    'mappedDefaultValue' in field
      ? (field.mappedDefaultValue as FieldMappingEditorValue)
      : undefined;
  if (!mappedDefault || typeof mappedDefault === 'string' || !mappedDefault.value) {
    return null;
  }
  return (
    <MappedPillSlot>
      <FieldMappingDisplay content={mappedDefault} textProps={{ font: 'regular14' }} />
    </MappedPillSlot>
  );
};

const MappedPillSlot = styled.span({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  '& *': {
    minWidth: 0,
    maxWidth: '100%',
  },
});

export const ChoiceGroupWidthFix = styled.div({
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
  '[class*="checkbox-container"], [role="radio"]': {
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
  },
  '[class*="checkbox-container"] > span, [role="radio"] > span': {
    flex: '1 1 0%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
  },
});

const PillOverlay = styled.div<{ $rightPadding?: string }>(({ $rightPadding }) => ({
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  height: theme.base.height,
  display: 'flex',
  alignItems: 'center',
  paddingLeft: pxToRem(12),
  paddingRight: $rightPadding ?? pxToRem(12),
  overflow: 'hidden',
  pointerEvents: 'none',
  '.node-wrapper': {
    pointerEvents: 'auto',
  },
}));

const PillContent = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;

  & > span {
    display: inline !important;
    white-space: nowrap !important;
    overflow-wrap: normal !important;
  }

  & > span > div {
    display: inline !important;
    white-space: nowrap !important;
    overflow-wrap: normal !important;
  }
`;

const PillContentMultiLine = styled.div({
  overflow: 'hidden',
  minWidth: 0,
  flex: 1,
});

type SelectDefaultValueMappingProps = {
  field: StateField;
};

export const SelectDefaultValueMapping = ({ field }: SelectDefaultValueMappingProps) => {
  const {
    actions,
    variableGroups,
    isInputForm,
    fieldType,
    mappedDefault,
    showStandardFieldMappingSidebar,
  } = useDefaultValueMappingBase(field, 'select');
  const selectionField = field as SelectionStateField;
  const isMulti = fieldType === 'multi_select';

  const filteredGroups = useMemo(() => {
    if (!variableGroups?.length) {
      return [];
    }
    const groups = filterVariableGroupsByType(variableGroups, fieldType);
    return groups
      .map((group) => ({
        ...group,
        fields: group.fields.filter(
          (f) => f.type === 'separator' || (f.field !== 'value' && f.field !== 'values')
        ),
      }))
      .filter((group) => group.fields.length > 0);
  }, [variableGroups, fieldType]);

  const fieldOptions = useFieldOptions(field as FieldContainingOptions);
  const optionsFromListId = selectionField.optionsFromListId;
  const prevOptionsFromListIdRef = useRef(optionsFromListId);

  useEffect(() => {
    if (prevOptionsFromListIdRef.current !== optionsFromListId) {
      const currentDefault = selectionField.defaultValue;
      const hasDefault = isMulti
        ? Array.isArray(currentDefault) && currentDefault.length > 0
        : currentDefault != null && currentDefault !== '';
      if (hasDefault) {
        actions.createChangeHandler(selectionField, 'defaultValue')(isMulti ? [] : null);
      }
      prevOptionsFromListIdRef.current = optionsFromListId;
    }
  }, [optionsFromListId, isMulti, actions, field, selectionField]);

  const isMapped =
    mappedDefault !== undefined &&
    typeof mappedDefault === 'object' &&
    Boolean(mappedDefault.value) &&
    mappedDefault.variables !== undefined &&
    Object.keys(mappedDefault.variables).length > 0;

  const handleMappedChange = (value: FieldMappingEditorValue) => {
    actions.createChangeHandler(selectionField, 'mappedDefaultValue')(value);
  };

  const setStaticDefault = (value: string) => {
    actions.createChangeHandler(
      selectionField,
      'defaultValue'
    )(isMulti ? (value ? [value] : []) : value || null);
  };

  const handleContent = (content: string) => {
    const manualValue = extractManualSelectValue(content);
    if (manualValue !== null) {
      handleMappedChange({ value: '', variables: {} });
      setStaticDefault(manualValue);
      return;
    }
    handleMappedChange(transformOutput(content));
  };
  const editorRef = useRef<EditorMethods>({
    insertContent: handleContent,
    replaceContent: handleContent,
  }) as RefObject<EditorMethods>;

  const customShowFieldMappingSidebar: (props?: { variable?: FieldMappingFieldItem }) => void = (
    props
  ) => {
    showStandardFieldMappingSidebar({
      editorRef,
      variableGroups: filteredGroups,
      fieldMappingId: `${field.id}-default`,
      fieldType: fieldType as MappingFieldType,
      label: t('defaultValue'),
      isStandardSidebar: true,
      manualData: fieldOptions.length > 0 ? fieldOptions : undefined,
      ...props,
    });
  };

  const clearMapping = () => {
    handleMappedChange({ value: '', variables: {} });
  };

  if (!isInputForm) {
    return null;
  }

  const defaultValue = selectionField.defaultValue;
  const selectedMultiOptions = isMulti
    ? fieldOptions.filter((opt) => Array.isArray(defaultValue) && defaultValue.includes(opt.value))
    : [];

  const mappedOption = isMapped
    ? {
        value: MAPPED_OPTION_VALUE,
        label: (
          <PillClickGuard
            onMouseDown={(event) => event.stopPropagation()}
            onClick={(event) => event.stopPropagation()}
          >
            <FieldMappingDisplay
              content={mappedDefault}
              textProps={{ font: 'regular14' }}
              enableExpandable
              showFieldMappingSidebar={customShowFieldMappingSidebar}
            />
          </PillClickGuard>
        ),
      }
    : null;

  const handleStaticSingleChange = (value: string) => {
    if (value === MAPPED_OPTION_VALUE) return;
    if (isMapped) clearMapping();
    setStaticDefault(value);
  };

  return (
    <Stack space={2}>
      <Text font="semibold14" color="textSecondary" text={t('defaultValue')} />
      <SelectMappingControl>
        {isMulti && !isMapped ? (
          <SelectMulti
            options={fieldOptions}
            value={selectedMultiOptions}
            onChange={(opts) =>
              actions.createChangeHandler(selectionField, 'defaultValue')(opts.map((o) => o.value))
            }
            menuPortalTarget={document.body}
            placeholder={t('selectPlaceholder')}
          />
        ) : (
          <Select
            options={mappedOption ? [mappedOption, ...fieldOptions] : fieldOptions}
            value={isMapped ? MAPPED_OPTION_VALUE : (defaultValue as string) ?? null}
            onChange={handleStaticSingleChange}
            isClearable
            placeholder={t('selectPlaceholder')}
            filterOption={(option) => option.value !== MAPPED_OPTION_VALUE}
            styles={{
              indicatorsContainer: (provided) => ({ ...provided, marginRight: pxToRem(36) }),
            }}
          />
        )}
        <PlusButtonOverlay>
          <IconButton
            icon={<IconAddSmall />}
            variant="blue-white"
            size="sm"
            onClick={() => customShowFieldMappingSidebar()}
            data-cy={`${field.kind}-default-mapping-open`}
          />
        </PlusButtonOverlay>
      </SelectMappingControl>
    </Stack>
  );
};

export const MAPPED_OPTION_VALUE = '__mapped_default__';

const SelectMappingControl = styled.div({
  position: 'relative',
  width: '100%',
  minWidth: 0,
  '[class*="-ValueContainer"]': {
    paddingRight: pxToRem(40),
    overflow: 'hidden',
  },
  '[class*="-singleValue"]': {
    overflow: 'hidden',
  },
});

const PlusButtonOverlay = styled.div({
  position: 'absolute',
  top: '50%',
  right: pxToRem(8),
  transform: 'translateY(-50%)',
  zIndex: 1,
});

const PillClickGuard = styled.span({
  display: 'flex',
  alignItems: 'center',
  minWidth: 0,
  maxWidth: '100%',
  width: '100%',
  overflow: 'hidden',
  '& *': {
    minWidth: 0,
    maxWidth: '100%',
  },
});

const { t } = getScopedTranslate({
  en: {
    defaultValue: 'Default value',
    selectPlaceholder: 'Select an option',
    maxLengthError:
      'Single line text is limited to {limit} characters. Convert to long text for more.',
  },
});
